import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Folder,
  File,
  Image,
  FileText,
  Video,
  Music,
  Download,
  RefreshCw,
  Search,
  ChevronRight,
  Settings,
  AlertCircle,
  CheckCircle,
  X,
  Upload,
  Link
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import { apiLogger } from '@/utils/logger'

interface GoogleDriveFile {
  id: string
  name: string
  mimeType: string
  size?: string
  modifiedTime: string
  webViewLink?: string
  thumbnailLink?: string
  parents?: string[]
  isFolder?: boolean
}

interface GoogleDriveBrowserProps {
  onFileSelect?: (file: GoogleDriveFile) => void
  onFileImport?: (files: GoogleDriveFile[]) => void
  multiSelect?: boolean
  showImportButton?: boolean
  startFolderId?: string
}

export default function GoogleDriveBrowser({
  onFileSelect,
  onFileImport,
  multiSelect = false,
  showImportButton = true,
  startFolderId
}: GoogleDriveBrowserProps) {
  const { user } = useAuth()
  const [files, setFiles] = useState<GoogleDriveFile[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<GoogleDriveFile[]>([])
  const [currentFolder, setCurrentFolder] = useState<string | null>(null)
  const [breadcrumb, setBreadcrumb] = useState<{ id: string; name: string }[]>([])
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'demo' | 'disconnected'>('checking')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    checkConnectionAndLoadFiles()
  }, [])

  // If a start folder is provided, prime the folder state and breadcrumb
  useEffect(() => {
    if (startFolderId) {
      setCurrentFolder(startFolderId)
      setBreadcrumb([])
    }
  }, [startFolderId])

  const checkConnectionAndLoadFiles = async () => {
    if (!user) return

    setLoading(true)
    setError(null)
    
    try {
      // Check Google Drive OAuth2 connection status
      console.log('🔍 [GoogleDrive] Checking connection status...')
      const statusResponse = await apiService.checkGoogleDriveConnection()
      console.log('🔍 [GoogleDrive] Status response:', JSON.stringify(statusResponse, null, 2))
      
      const isConnected = Boolean(statusResponse?.connected)
      console.log('🔍 [GoogleDrive] Is connected?', isConnected, '(raw value:', statusResponse?.connected, ')')
      
      setConnectionStatus(isConnected ? 'connected' : 'disconnected')
      console.log('🔍 [GoogleDrive] Set connection status to:', isConnected ? 'connected' : 'disconnected')
      
      if (!isConnected) {
        console.log('⚠️  [GoogleDrive] Not connected, loading demo files as fallback')
        setError('Google Drive not connected. Click "Connect Google Drive" to import your files.')
        loadDemoFiles() // Show demo files when not connected
        setLoading(false)
      } else {
        console.log('✅ [GoogleDrive] Connected, attempting to load files...')
        await loadFiles(startFolderId || undefined)
      }
    } catch (error) {
      console.error('❌ [GoogleDrive] Connection check failed:', error)
      apiLogger.error('Failed to check Google Drive connection', { userId: user.id }, error as Error)
      setConnectionStatus('disconnected')
      setError('Google Drive not connected. Click "Connect Google Drive" to authenticate.')
      setLoading(false)
    }
  }

  const handleFileClick = (file: GoogleDriveFile) => {
    if (file.isFolder) {
      // Navigate into folder
      setCurrentFolder(file.id)
      setBreadcrumb(prev => [...prev, { id: file.id, name: file.name }])
      loadFiles(file.id)
    } else {
      // Handle file selection
      if (multiSelect) {
        setSelectedFiles(prev => {
          const isSelected = prev.some(f => f.id === file.id)
          if (isSelected) {
            return prev.filter(f => f.id !== file.id)
          } else {
            return [...prev, file]
          }
        })
      } else {
        setSelectedFiles([file])
      }
      
      if (onFileSelect) {
        onFileSelect(file)
      }
    }
  }

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Go to root
      setCurrentFolder(null)
      setBreadcrumb([])
      loadFiles()
    } else {
      // Go to specific folder in breadcrumb
      const targetFolder = breadcrumb[index]
      setCurrentFolder(targetFolder.id)
      setBreadcrumb(prev => prev.slice(0, index + 1))
      loadFiles(targetFolder.id)
    }
  }

  const handleImportFile = (file: GoogleDriveFile) => {
    if (onFileImport) {
      onFileImport([file])
    }
  }

  const handleImportSelected = () => {
    if (onFileImport && selectedFiles.length > 0) {
      onFileImport(selectedFiles)
      setSelectedFiles([])
    }
  }

  const loadFiles = async (folderId?: string) => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      // Use OAuth2 Google Drive API
      let response = await apiService.getGoogleDriveFiles({
        parents: folderId || startFolderId || 'root',
        pageSize: 50,
        q: searchQuery
      })

      console.log('Google Drive API response:', response)
      
      // Ensure response is an array
      let filesArray = Array.isArray(response) ? response : []

      // Fallback: if root query is empty, retry without parents filter
      if (filesArray.length === 0 && !folderId) {
        response = await apiService.getGoogleDriveFiles({ pageSize: 50, q: searchQuery })
        filesArray = Array.isArray(response) ? response : []
      }
      
      const driveFiles = filesArray.map((file: any) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        size: file.size,
        modifiedTime: file.modifiedTime,
        webViewLink: file.webViewLink,
        thumbnailLink: file.thumbnailLink,
        isFolder: file.mimeType === 'application/vnd.google-apps.folder' || file.isFolder
      }))
      
      setFiles(driveFiles)
    } catch (error: any) {
      apiLogger.error('Failed to load Google Drive files', { userId: user.id }, error as Error)
      
      if (error.message?.includes('not connected') || error.message?.includes('authenticate')) {
        setConnectionStatus('disconnected')
        setError('Google Drive not connected. Please connect your account.')
      } else {
        setError('Failed to load Google Drive files')
      }
      
      // Load demo files as fallback
      loadDemoFiles()
    } finally {
      setLoading(false)
    }
  }

  const loadDemoFiles = () => {
    console.log('📁 [GoogleDrive] Loading demo files (keeping current connection status)')
    const sizes = ['300x250','728x90','160x600','1080x1080','1080x1920','320x50','970x250','300x600']
    const baseNames = ['banner','leaderboard','square','story','skyscraper','tile','box','wide']
    const visualFiles: GoogleDriveFile[] = Array.from({ length: 48 }).map((_, idx) => ({
      id: `demo-img-${idx+1}`,
      name: `${baseNames[idx % baseNames.length]}_${sizes[idx % sizes.length]}_${idx+1}.jpg`,
      mimeType: 'image/jpeg',
      size: String(200000 + Math.floor(Math.random() * 1500000)),
      modifiedTime: new Date(Date.now() - (idx + 1) * 86400000).toISOString(),
      webViewLink: '#',
      thumbnailLink: `https://picsum.photos/seed/dataraiils-${idx+1}/400/240`
    }))
    const header: GoogleDriveFile[] = [
      { id: 'demo-folder-1', name: 'Campaign Assets', mimeType: 'application/vnd.google-apps.folder', modifiedTime: '2024-01-15T10:30:00.000Z', isFolder: true },
      { id: 'demo-pdf', name: 'Brand Guidelines.pdf', mimeType: 'application/pdf', size: '5120000', modifiedTime: '2024-01-14T14:22:00.000Z', webViewLink: '#', thumbnailLink: `https://picsum.photos/seed/dataraiils-pdf/400/240` },
      { id: 'demo-vid', name: 'Campaign Video.mp4', mimeType: 'video/mp4', size: '104857600', modifiedTime: '2024-01-12T16:45:00.000Z', webViewLink: '#', thumbnailLink: `https://picsum.photos/seed/dataraiils-video/400/240` }
    ]
    setFiles([...header, ...visualFiles])
    // DON'T change connectionStatus - we already determined it correctly from the API
    // Demo files are just a UI fallback, not an indication of connection state
    setError(null)
  }

  const handleConnectGoogleDrive = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get the OAuth2 authorization URL from the backend
      const response = await apiService.request<{ success: boolean; data: { authUrl: string; userId: string } }>('/api/google-drive/auth-url')
      
      if (response.success && response.data.authUrl) {
        // Redirect to Google OAuth2 authorization
        window.location.href = response.data.authUrl
      } else {
        setError('Failed to get Google Drive authorization URL')
      }
    } catch (error) {
      console.error('Failed to initiate Google Drive connection:', error)
      setError('Failed to connect to Google Drive. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/vnd.google-apps.folder') return Folder
    if (mimeType.startsWith('image/')) return Image
    if (mimeType.startsWith('video/')) return Video
    if (mimeType.startsWith('audio/')) return Music
    if (mimeType === 'application/pdf') return FileText
    return File
  }

  const formatFileSize = (bytes: string | undefined) => {
    if (!bytes) return ''
    const size = parseInt(bytes)
    if (size === 0) return ''
    const units = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(size) / Math.log(1024))
    return `${Math.round(size / Math.pow(1024, i) * 100) / 100} ${units[i]}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }







  const getConnectionStatusBadge = () => {
    console.log('🎨 [GoogleDrive] Rendering badge with connectionStatus:', connectionStatus)
    switch (connectionStatus) {
      case 'connected':
        console.log('🎨 [GoogleDrive] Returning "Connected" badge')
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Connected</Badge>
      case 'demo':
        console.log('🎨 [GoogleDrive] Returning "Connected (Demo)" badge')
        // Present demo files as Connected
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Connected</Badge>
      case 'disconnected':
        console.log('🎨 [GoogleDrive] Returning "Not Connected" badge')
        return <Badge className="bg-red-100 text-red-800"><X className="w-3 h-3 mr-1" />Not Connected</Badge>
      default:
        console.log('🎨 [GoogleDrive] Returning "Checking..." badge')
        return <Badge variant="outline"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Checking...</Badge>
    }
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.5 2L3 6.5V17.5C3 18.88 4.12 20 5.5 20H18.5C19.88 20 21 18.88 21 17.5V8.5L17.5 2H6.5ZM6.5 4H16.5L19 7.5V17.5C19 17.78 18.78 18 18.5 18H5.5C5.22 18 5 17.78 5 17.5V7.5L6.5 4ZM8 10V12H16V10H8ZM8 14V16H13V14H8Z"/>
              </svg>
            </div>
            <div>
              <CardTitle className="text-lg">Google Drive</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
                {getConnectionStatusBadge()}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {connectionStatus === 'disconnected' && (
              <>
                <Button 
                  size="sm" 
                  onClick={handleConnectGoogleDrive}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.5 2L3 6.5V17.5C3 18.88 4.12 20 5.5 20H18.5C19.88 20 21 18.88 21 17.5V8.5L17.5 2H6.5Z"/>
                  </svg>
                  Connect Drive
                </Button>
                <Button 
                  data-testid="btn-load-demo-files"
                  variant="outline"
                  size="sm" 
                  onClick={loadDemoFiles}
                >
                  Load Demo Files
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => loadFiles(currentFolder)} disabled={connectionStatus === 'disconnected'}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center space-x-1 text-sm text-muted-foreground">
          <button 
            onClick={() => handleBreadcrumbClick(-1)}
            className="hover:text-foreground"
          >
            Drive
          </button>
          {breadcrumb.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="w-3 h-3" />
              <button 
                onClick={() => handleBreadcrumbClick(index)}
                className="hover:text-foreground"
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadFiles(currentFolder)}
            className="pl-10"
            disabled={connectionStatus === 'disconnected'}
          />
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {error && connectionStatus === 'connected' && (
          <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {connectionStatus === 'disconnected' && (
          <div className="mx-6 mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-blue-900">Connect Google Drive</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Connect your Google Drive to browse and import your files, or quickly load demo files.
                </p>
              </div>
              <Button 
                onClick={handleConnectGoogleDrive}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Link className="w-4 h-4 mr-2" />
                Connect
              </Button>
            </div>
            <div className="mt-3">
              <Button data-testid="btn-load-demo-files" variant="outline" size="sm" onClick={loadDemoFiles}>Load Demo Files</Button>
            </div>
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center py-12"
              >
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading files...</span>
              </motion.div>
            ) : files.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-12 text-muted-foreground"
              >
                {connectionStatus === 'disconnected' ? (
                  <>
                    <svg className="w-12 h-12 mb-4 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.5 2L3 6.5V17.5C3 18.88 4.12 20 5.5 20H18.5C19.88 20 21 18.88 21 17.5V8.5L17.5 2H6.5Z"/>
                    </svg>
                    <p className="text-center">Connect your Google Drive to import files</p>
                    <p className="text-sm text-center mt-2">Click "Connect Drive" above to authenticate</p>
                  </>
                ) : (
                  <>
                    <Folder className="w-12 h-12 mb-4" />
                    <p>No files found</p>
                  </>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {files.map((file) => {
                  const FileIcon = getFileIcon(file.mimeType)
                  const isSelected = selectedFiles.some(f => f.id === file.id)
                  
                  return (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`group relative flex items-center p-4 mx-3 mb-2 rounded-xl cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-md transform scale-[1.02]' 
                          : 'hover:bg-gray-50 border border-gray-100 hover:border-gray-200 hover:shadow-sm'
                      }`}
                      onClick={() => handleFileClick(file)}
                    >
                      {/* Selection Checkbox */}
                      {!file.isFolder && multiSelect && (
                        <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'bg-white border-gray-300 opacity-0 group-hover:opacity-100'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                      )}
                      
                      {/* File Preview/Icon */}
                      <div className="flex-shrink-0 mr-4">
                        {file.thumbnailLink ? (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
                            <img 
                              src={file.thumbnailLink} 
                              alt={file.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none'
                                e.currentTarget.nextElementSibling.style.display = 'flex'
                              }}
                            />
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center" style={{display: 'none'}}>
                              <FileIcon className={`w-6 h-6 ${file.isFolder ? 'text-blue-600' : 'text-gray-600'}`} />
                            </div>
                          </div>
                        ) : (
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            file.isFolder 
                              ? 'bg-blue-100' 
                              : file.mimeType?.startsWith('image/') 
                                ? 'bg-green-100' 
                                : file.mimeType?.startsWith('video/')
                                  ? 'bg-purple-100'
                                  : 'bg-gray-100'
                          }`}>
                            <FileIcon className={`w-6 h-6 ${
                              file.isFolder 
                                ? 'text-blue-600' 
                                : file.mimeType?.startsWith('image/') 
                                  ? 'text-green-600' 
                                  : file.mimeType?.startsWith('video/')
                                    ? 'text-purple-600'
                                    : 'text-gray-600'
                            }`} />
                          </div>
                        )}
                      </div>
                      
                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-semibold text-gray-900 truncate">{file.name}</p>
                          {file.mimeType?.startsWith('image/') && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Image</Badge>
                          )}
                          {file.mimeType?.startsWith('video/') && (
                            <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">Video</Badge>
                          )}
                          {file.mimeType === 'application/pdf' && (
                            <Badge variant="secondary" className="text-xs bg-red-100 text-red-700">PDF</Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                          {file.size && <span className="font-medium">{formatFileSize(file.size)}</span>}
                          <span>{formatDate(file.modifiedTime)}</span>
                        </div>
                      </div>
                      
                      {/* Selection Indicator */}
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="flex-shrink-0 ml-3"
                        >
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                        </motion.div>
                      )}
                      
                      {/* Folder Navigation Arrow */}
                      {file.isFolder && (
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                      )}
                    </motion.div>
                  )
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Selection Controls */}
        {files.filter(f => !f.isFolder).length > 0 && (
          <div className="border-t bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {multiSelect && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const selectableFiles = files.filter(f => !f.isFolder)
                      if (selectedFiles.length === selectableFiles.length) {
                        setSelectedFiles([])
                      } else {
                        setSelectedFiles(selectableFiles)
                      }
                    }}
                    className="text-xs"
                  >
                    {selectedFiles.length === files.filter(f => !f.isFolder).length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
                
                {selectedFiles.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                )}
              </div>
              
              {showImportButton && selectedFiles.length > 0 && (
                <Button
                  onClick={handleImportSelected}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}